import React, { useRef, useState } from "react";
import DebounceTextarea from "components/DebounceTextarea";
import SectionBezel from "components/SectionBezel";
import { SHORT_LINK_SETTINGS } from "utils/constants/shortLink";
import { STATE_KEYS } from "utils/constants/state";
import { getFieldPath } from "utils/form";
import { useFallbackRedirectUrls } from "utils/hooks";
import get from "lodash/get";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import { useFormikContext } from "formik";
import { BUTTON_CLASS_CONTEXT, UI_THEME } from "utils/constants/ui";
import Button from "components/Form/fields/Button";
import { getUUID } from "utils/uuid";

const getInputKeys = () => ({
  successUrl: getUUID(),
  cancelUrl: getUUID()
});

const RedirectsField = ({
  name,
  fieldset = [
    SHORT_LINK_SETTINGS.CHECKOUT_SUCCESS,
    SHORT_LINK_SETTINGS.CHECKOUT_CANCEL,
    SHORT_LINK_SETTINGS.SHIPPING_DESTINATIONS
  ],
  children
}) => {
  const successRef = useRef(null);
  const cancelRef = useRef(null);
  const nodes = [];
  const {
    successUrl: defaultSuccessUrl,
    cancelUrl: defaultCancelUrl
  } = useFallbackRedirectUrls();
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched,
    setValues,
    setTouched
  } = useFormikContext();

  /**
   * Initialize some uids for the textareas
   * - refresh them on reset so that the components know to unset their inner state
   */
  const [inputKeys, setInputKeys] = useState(getInputKeys());

  const successNamePath = getFieldPath(name, STATE_KEYS.SHORT_LINK.SUCCESS_URL);
  const cancelNamePath = getFieldPath(name, STATE_KEYS.SHORT_LINK.CANCEL_URL);
  const currentSuccess = get(values, successNamePath);
  const currentCancel = get(values, cancelNamePath);

  const canSetRedirectDefaults =
    currentSuccess !== defaultSuccessUrl || currentCancel !== defaultCancelUrl;

  const showSuccessUrl =
    fieldset.indexOf(SHORT_LINK_SETTINGS.CHECKOUT_SUCCESS) >= 0;
  const showCancelUrl =
    fieldset.indexOf(SHORT_LINK_SETTINGS.CHECKOUT_CANCEL) >= 0;

  if (showSuccessUrl) {
    nodes.push(
      <DebounceTextarea
        name={successNamePath}
        label="Success URL"
        placeholder={defaultSuccessUrl}
        minRows={1}
        uid={inputKeys.successUrl}
        inputValue={currentSuccess}
        debounce={500}
        innerRef={successRef}
        valueSet={(val) => {
          setFieldValue(successNamePath, val);

          const isTouched = get(touched, successNamePath);
          if (!isTouched) {
            setTimeout(() => {
              setFieldTouched(successNamePath, true);
            }, 0);
          }
        }}
      />
    );
  }
  if (showCancelUrl) {
    nodes.push(
      <DebounceTextarea
        name={cancelNamePath}
        label="Cancel URL"
        minRows={1}
        placeholder={defaultCancelUrl}
        inputValue={currentCancel}
        debounce={500}
        uid={inputKeys.cancelUrl}
        innerRef={cancelRef}
        valueSet={(val) => {
          setFieldValue(cancelNamePath, val);

          const isTouched = get(touched, cancelNamePath);
          if (!isTouched) {
            setTimeout(() => {
              setFieldTouched(cancelNamePath, true);
            }, 0);
          }
        }}
      />
    );
  }

  const setDefaultsAction = (
    <Button
      theme={UI_THEME.SLIM}
      copy="Use defaults"
      disabled={!canSetRedirectDefaults}
      classes={{
        contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
      }}
      onClick={() => {
        const clonedValues = cloneDeep(values);
        const clonedTouched = cloneDeep(touched);

        if (showSuccessUrl) {
          set(clonedValues, successNamePath, defaultSuccessUrl);
          set(clonedTouched, successNamePath, true);
        }
        if (showCancelUrl) {
          set(clonedValues, cancelNamePath, defaultCancelUrl);
          set(clonedTouched, cancelNamePath, true);
        }

        setValues(clonedValues);
        setTouched(clonedTouched);

        /**
         * Reset keys to trigger textarea inner value reset
         */
        setTimeout(() => {
          setInputKeys(getInputKeys());
        }, 0);
      }}
    />
  );

  return (
    <SectionBezel header="Redirects" expandable>
      <div className="w-100 justify-end flex pb2">{setDefaultsAction}</div>
      {nodes.map((node, nodeIx) => (
        <div key={nodeIx} className={`w-100 ${nodeIx ? "pt3" : ""}`}>
          {node}
        </div>
      ))}
      {children}
    </SectionBezel>
  );
};

export default RedirectsField;
